<app-vault-module-shell
  moduleLabel="Perfil"
  [accessDenied]="accessDenied()"
  [loadError]="loadError()"
  [loadErrorTitle]="loadErrorTitle()"
>
  <section class="user-profile">
    @if (loading()) {
      <div class="user-profile__skeleton" aria-busy="true">
        <lib-skeleton variant="rectangle" [height]="'56px'" />
        <lib-skeleton variant="rectangle" [height]="'56px'" />
        <lib-skeleton variant="rectangle" [height]="'120px'" />
      </div>
    } @else {
      <div class="user-profile__form-card">
        <header class="user-profile__form-head">
          <h2 id="user-profile-form-title">Mi perfil</h2>
          <p class="user-profile__form-intro">{{ formIntroText() }}</p>
        </header>

        @if (info()) {
          <app-vault-page-alert
            type="success"
            title="Listo"
            [message]="info()"
            (closed)="info.set(null)"
          />
        }
        @if (error()) {
          <app-vault-page-alert
            type="error"
            title="Error"
            [message]="error()"
            (closed)="error.set(null)"
          />
        }

        <form
          class="user-profile__form-shell"
          autocomplete="off"
          novalidate
          aria-labelledby="user-profile-form-title"
          (submit)="$event.preventDefault()"
        >
          <app-grid-form preset="2cols" [columns]="2" gap="1rem" wrapperClass="user-profile__form">
            <app-grid-form-item [span]="1">
              <app-field-label
                label="Nombre para mostrar"
                [tooltip]="fullNameFieldTooltip()"
              />
              <lib-input
                label=""
                [placeholder]="savedFullName() || 'Nombre y apellidos'"
                vaultInputAutocomplete="off"
                vaultInputReadonlyUntilFocus
                vaultInputIgnorePasswordManagers
                [vaultInputName]="'vault-profile-display-' + formAutofillNonce()"
                [value]="fullName()"
                (valueChange)="fullName.set($event)"
              />
              @if (savedFullName()) {
                <p class="user-profile__hint">Nombre actual: {{ savedFullName() }}</p>
              }
            </app-grid-form-item>

            <app-grid-form-item [span]="1">
              <app-field-label label="Correo electrónico" [tooltip]="emailFieldTooltip()" />
              <lib-input
                label=""
                type="text"
                readonly
                [disabled]="true"
                [value]="email()"
              />
            </app-grid-form-item>

            <app-grid-form-item [span]="1">
              <app-field-label label="Usuario" [tooltip]="loginAccessFieldTooltip()" />
              <div class="user-profile__login-username-field">
                <lib-input
                  label=""
                  readonly
                  [disabled]="true"
                  [value]="loginUsername()"
                  placeholder="—"
                />
              </div>
            </app-grid-form-item>

            @if (!isSuperadminProfile()) {
              <app-grid-form-item [span]="1">
                <app-field-label label="Empresa" [tooltip]="companyFieldTooltip()" />
                <p class="user-profile__hint user-profile__readonly-value">{{ companyLabel() }}</p>
              </app-grid-form-item>
              <app-grid-form-item [span]="1">
                <app-field-label label="Rol Vault" [tooltip]="roleFieldTooltip()" />
                <p class="user-profile__hint user-profile__readonly-value">{{ roleLabel() }}</p>
              </app-grid-form-item>
            }
          </app-grid-form>

          <div
            class="user-profile__secret-panel"
            role="group"
            aria-labelledby="user-profile-edit-secret-label"
          >
            @if (canUseAdminPasswordReset()) {
              <p class="user-profile__hint user-profile__hint--contract">
                Como administrador no necesita la clave anterior: el sistema la envía vacía (reset
                admin). Solo complete la nueva clave si desea cambiarla.
              </p>
              <div class="user-profile__password-reset-grid">
                <app-field-label
                  id="user-profile-edit-current-password-label"
                  label="Contraseña actual del usuario"
                  tooltip="Reset admin: no se solicita ni se envía la clave anterior."
                />
                <div
                  class="user-profile__password-reset-static"
                  aria-labelledby="user-profile-edit-current-password-label"
                >
                  <span class="user-profile__password-reset-static-value">No requerida (reset admin)</span>
                </div>
                <app-field-label
                  id="user-profile-edit-secret-label"
                  label="Nueva clave de acceso (opcional)"
                  [tooltip]="adminPasswordFieldTooltip()"
                />
                <app-admin-user-secret-field
                  [fieldKey]="formAutofillNonce()"
                  [resetToken]="secretFieldResetToken()"
                  [reveal]="showFormPassword()"
                  placeholder="Dejar vacío para no cambiar"
                  ariaLabel="Nueva clave de acceso"
                  (valueChange)="formPassword.set($event)"
                  (toggleReveal)="toggleFormPasswordVisibility()"
                />
              </div>
              <p class="user-profile__hint">
                Deje la nueva clave vacía para conservar la actual.
              </p>
            } @else {
              <p class="user-profile__hint user-profile__hint--contract">
                Para cambiar su clave debe ingresar la contraseña actual y la nueva clave de acceso.
              </p>
              <div class="user-profile__password-reset-grid">
                <app-field-label label="Contraseña actual" />
                <app-admin-user-secret-field
                  [fieldKey]="formAutofillNonce() + '-current'"
                  [resetToken]="secretFieldResetToken()"
                  [reveal]="showCurrentPassword()"
                  placeholder="Toque aquí para ingresar su clave actual"
                  ariaLabel="Contraseña actual"
                  (valueChange)="currentPassword.set($event)"
                  (toggleReveal)="toggleCurrentPasswordVisibility()"
                />
                <app-field-label
                  label="Nueva clave de acceso (opcional)"
                  [tooltip]="selfPasswordFieldTooltip()"
                />
                <app-admin-user-secret-field
                  [fieldKey]="formAutofillNonce() + '-new'"
                  [resetToken]="secretFieldResetToken()"
                  [reveal]="showFormPassword()"
                  placeholder="Toque aquí para ingresar la nueva clave"
                  ariaLabel="Nueva clave de acceso"
                  (valueChange)="formPassword.set($event)"
                  (toggleReveal)="toggleFormPasswordVisibility()"
                />
              </div>
              <p class="user-profile__hint">
                Toque cada campo para ingresar la clave (evita autocompletado del navegador). Deje
                ambos vacíos para conservar la actual.
              </p>
            }
            <app-admin-user-password-rules [password]="formPassword()" />
            <p class="user-profile__hint">
              Para ingresar al sistema use el usuario de acceso
              <strong>{{ loginUsername() }}</strong>, no el correo completo.
            </p>
          </div>

          <footer class="user-profile__form-footer">
            <lib-button
              variant="primary"
              type="button"
              [loading]="saving()"
              [disabled]="profileSaveDisabled()"
              (clicked)="saveChanges()"
            >
              Guardar cambios
            </lib-button>
          </footer>
        </form>
      </div>
    }
  </section>
</app-vault-module-shell>
